import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetchJson } from "@/lib/authenticatedFetch";
import type {
  CUBalanceResponse,
  CUCostsResponse,
  CURunsResponse,
  CUUsageByWorkflowResponse,
  CUUsageResponse,
} from "@/types/cu";
import { useQuery } from "@tanstack/react-query";

// Query keys
export const cuKeys = {
  all: ["cu"] as const,
  balance: (orgId: string) => [...cuKeys.all, "balance", orgId] as const,
  usage: (orgId: string, yearMonth: string) => [...cuKeys.all, "usage", orgId, yearMonth] as const,
  costs: () => [...cuKeys.all, "costs"] as const,
  runs: (orgId: string) => [...cuKeys.all, "runs", orgId] as const,
  usageByWorkflow: (orgId: string, from: number, to: number) =>
    [...cuKeys.all, "usage-by-workflow", orgId, from, to] as const,
};

/**
 * Hook for fetching CU balance for an organization
 */
export function useCUBalance(organizationId?: string) {
  return useQuery({
    queryKey: cuKeys.balance(organizationId || ""),
    queryFn: async () => {
      const response = await authenticatedFetchJson<CUBalanceResponse>(
        `${V2_ENDPOINT}/v1/organizations/${organizationId}/cu`,
        { orgId: organizationId },
      );
      return {
        balance: parseFloat(response.data.balance),
        minThreshold: parseFloat(response.data.minThreshold),
      };
    },
    enabled: !!organizationId,
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Auto-refresh every 60 seconds
  });
}

/**
 * Hook for fetching CU usage analytics for an organization
 */
export function useCUUsage(organizationId?: string, yearMonth?: string) {
  return useQuery({
    queryKey: cuKeys.usage(organizationId || "", yearMonth || ""),
    queryFn: async () => {
      const response = await authenticatedFetchJson<CUUsageResponse>(
        `${V2_ENDPOINT}/v1/organizations/${organizationId}/cu/usage?yearMonth=${yearMonth}`,
        { orgId: organizationId },
      );
      return response.data;
    },
    enabled: !!organizationId && !!yearMonth,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook for fetching recent runs with CU costs
 */
export function useCURuns(organizationId?: string, limit = 10) {
  return useQuery({
    queryKey: cuKeys.runs(organizationId || ""),
    queryFn: async () => {
      const response = await authenticatedFetchJson<CURunsResponse>(
        `${V2_ENDPOINT}/v1/organizations/${organizationId}/cu/runs?limit=${limit}`,
        { orgId: organizationId },
      );
      return response.data;
    },
    enabled: !!organizationId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook for fetching CU cost schedule (public endpoint)
 */
export function useCUCosts() {
  return useQuery({
    queryKey: cuKeys.costs(),
    queryFn: async () => {
      const response = await fetch(`${V2_ENDPOINT}/v1/cu/costs`);
      if (!response.ok) {
        throw new Error(`Failed to fetch CU costs: ${response.status}`);
      }
      const data: CUCostsResponse = await response.json();
      return data.data;
    },
    staleTime: 60000 * 5, // 5 minutes (rarely changes)
  });
}

/**
 * Hook for fetching per-workflow CU usage breakdown
 */
export function useCUUsageByWorkflow(organizationId?: string, from?: number, to?: number) {
  return useQuery({
    queryKey: cuKeys.usageByWorkflow(organizationId || "", from || 0, to || 0),
    queryFn: async () => {
      const response = await authenticatedFetchJson<CUUsageByWorkflowResponse>(
        `${V2_ENDPOINT}/v1/organizations/${organizationId}/cu/usage-by-workflow?from=${from}&to=${to}`,
        { orgId: organizationId },
      );
      return response.data;
    },
    enabled: !!organizationId && !!from && !!to,
    staleTime: 30000, // 30 seconds
  });
}
